import React, {useEffect, useState, useRef} from "react";
import {useLazyQuery} from "@apollo/client";
import {DataGridPro, useGridApiRef, GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import {Stack} from "@mui/material";

import {
    ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION,
} from "../queries/GraphQLQueries";
import TooltipLabel from "../components/common/TooltipLabel";
import AccreditationCheckBox from "../components/accreditation/AccreditationCheckBox";
import buildContentAccreditationHierarchy, {fetchOriginalIdsForNode} from "../helpers/AccreditationHelper";
import {NODE_LEVEL} from "../domain/HierarchyConstants";
import {ACCREDITATION_STATUS} from "../domain/AccreditationStatus";
import CustomAlert from "../components/CustomAlert";
import ContentHierarchyGroupingCell from "../components/common/ContentHierarchyGroupingCell";
import HierarchyActionButtons from "../components/common/HierarchyActionButtons";
import UpdateAccreditationDialog from "../components/accreditation/UpdateAccreditationDialog";
import Loader from "../components/Loader";
import {shouldGroupExpand} from "../helpers/ContentHierarchyDataHelper";

export const ACCREDITATION_ACTION_CREATE = 'Add';
export const ACCREDITATION_ACTION_REMOVE = 'Remove';

export default function AccreditationHierarchy() {

    const dataGridApi = useGridApiRef();

    const [fetchContentAndAccreditation, {loading, data}] = useLazyQuery(ALL_PERFORMERS_CONTENT_WITH_SERVICE_TYPE_AND_ACCREDITATION,
      {fetchPolicy: 'network-only'}
    );

    const [expandedState, setExpandedState] = useState(new Set());
    const [synthesizedColumns, setSynthesizedColumns] = useState([]);
    const [synthesizedRows, setSynthesizedRows] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [domainUserId, setDomainUserId] = useState(null);
    const [domainUserName, setDomainUserName] = useState(null);
    const [currentHierarchy, setCurrentHierarchy] = useState(null);
    const [accreditationAction, setAccreditationAction] = useState(null);
    const [nodeType, setNodeType] = useState(null);
    const [contentInfoIds, setContentInfoIds] = useState([]);

    const detailPanelExpandedRowIds = useRef([]);
    const isUpdateComplete = useRef(false);

    const expandAll = () => {
        setExpandedState(new Set(synthesizedRows.map(row => row.id)));
        detailPanelExpandedRowIds.current = synthesizedRows.map(row => row.id);
    };

    const collapseAll = () => {
        setExpandedState(new Set());
        detailPanelExpandedRowIds.current = [];
    }

    const handleCloseDialog = () => {
        setDialogOpen(false);
        isUpdateComplete.current = true;
    }

    useEffect(() => {
        fetchContentAndAccreditation();
    }, []);

    useEffect(() => {
        if (!data || loading) {
            return;
        }

        const { accreditationHierarchy, serviceTypeHierarchyMap, contentInfoAccreditationMap } = buildContentAccreditationHierarchy(data);
        const performers = data.users_filtered.map(user => {
            return {
                field: user.id.toString(),
                headerName: user.displayName,
                width: 80,
                editable: true,
                renderHeader: (params) => <TooltipLabel label={params.colDef.headerName}/>,
                renderCell: (params) => <AccreditationCheckBox content={params} handleAccreditationUpdate={handleAccreditationUpdate} serviceTypeHierarchyMap={serviceTypeHierarchyMap} contentInfoAccreditationMap={contentInfoAccreditationMap} />,
                sortable: false,
                headerAlign: 'center'
            }
        });

        setSynthesizedColumns(performers);
        setSynthesizedRows(accreditationHierarchy);
    }, [data, loading]);

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const handleAccreditationUpdate = (event, content, serviceTypeHierarchyMap, contentInfoAccreditationMap) => {
        const userId = parseInt(content.field);
        setDomainUserName(content.colDef.headerName);
        setCurrentHierarchy(content.row.contentNameHierarchy.join(" / "));
        const nodeTypeText = content.row.nodeType;
        let contentInfoIdList = [];
        isUpdateComplete.current = "in-progress";

        if (nodeTypeText === NODE_LEVEL.CHILD) {
            contentInfoIdList.push(content.row.originalId);
        } else {
            contentInfoIdList = fetchOriginalIdsForNode(serviceTypeHierarchyMap.filter(data => data.id === content.row.serviceTypeId), content.id);
        }

        if (content.value === ACCREDITATION_STATUS.PARTIAL_ACCREDITATION) {
            const filteredContentAccreditationData = contentInfoAccreditationMap.filter(data => data.serviceTypeId === content.row.serviceTypeId && data.userId === userId)

            if (event.target.checked) {
                contentInfoIdList = contentInfoIdList.filter(validId => !filteredContentAccreditationData.find(data => data.contentInfoId === validId));
            } else {
                contentInfoIdList = contentInfoIdList.filter(validId => filteredContentAccreditationData.find(data => data.contentInfoId === validId));
            }
        }

        const accreditationActionText = event.target.checked ? ACCREDITATION_ACTION_CREATE : ACCREDITATION_ACTION_REMOVE;
        setAccreditationAction(accreditationActionText);

        setDomainUserId(userId);
        setContentInfoIds(contentInfoIdList);
        setNodeType(nodeTypeText);
        setDialogOpen(true);
    }

    if (loading) {
        return <Loader />;
    }

    const accreditationHierarchyGroupColumnDefinition = {
        headerName: "Accreditation Hierarchy",
        width: 250,
        renderCell: params => <ContentHierarchyGroupingCell
                                  detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                                  isUpdateComplete={isUpdateComplete}
                                  {...params} />
    }

    // This style freezes the top bar along with hierarchy header row when user scrolls down
    return <Stack style={{height: 'calc(100vh - 16px)'}}>
        <HierarchyActionButtons
          onRefresh={fetchContentAndAccreditation}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}/>

        <DataGridPro
            treeData
            sx={{"& .MuiDataGrid-cell": {display: "flex", alignItems: "center"}}}
            hideFooter={true}
            disableColumnMenu={true}
            disableMultipleColumnsSorting={true}
            apiRef={dataGridApi}
            isGroupExpandedByDefault={params => shouldGroupExpand(params, new Set([...detailPanelExpandedRowIds.current]))}
            rows={synthesizedRows}
            columns={synthesizedColumns}
            getTreeDataPath={row => row.hierarchy}
            groupingColDef={accreditationHierarchyGroupColumnDefinition}
            initialState={{
                pinnedColumns: {
                  left: [GRID_TREE_DATA_GROUPING_FIELD],
                },
            }} />

        <UpdateAccreditationDialog
            openUpdateAccreditationDialog={dialogOpen}
            handleClose={handleCloseDialog}
            contentInfoIds={contentInfoIds}
            domainUserName={domainUserName}
            accreditationAction={accreditationAction}
            nodeType={nodeType}
            setAccreditationAction={setAccreditationAction}
            domainUserId={domainUserId}
            currentHierarchy={currentHierarchy} />

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </Stack>;
}
